import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { toast } from '@workspace/ui';
import { useScope } from '@workspace/scope-context';
import {
  AdminShell,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  Surface,
  VerticalStack,
  WorksurfaceHeader,
  type CommerceSelectOptionViewModel,
} from '@workspace/silk-ui';
import { Mail, RefreshCw, Route, ShieldAlert } from 'lucide-react';

import { useSession } from '@/context/SessionContext';
import { useNotificationAccess } from '@/lib/useNotificationAccess';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  createNotificationChannel,
  createNotificationRecipientGroup,
  createNotificationRoute,
  deleteNotificationRecipientGroup,
  deleteNotificationRoute,
  listNotificationChannels,
  listNotificationRecipientGroups,
  listNotificationDeliveries,
  listNotificationRoutes,
  NotificationAdminError,
  type NotificationChannel,
  type NotificationChannelListParams,
  type NotificationChannelPayload,
  type NotificationDelivery,
  type NotificationDeliveryListParams,
  type NotificationListResponse,
  type NotificationRecipientGroup,
  type NotificationRecipientGroupListParams,
  type NotificationRecipientGroupPayload,
  type NotificationRoute,
  type NotificationRouteListParams,
  type NotificationRoutePayload,
  testNotificationChannel,
  updateNotificationChannel,
  updateNotificationRecipientGroup,
  updateNotificationRoute,
} from '@/lib/notifications';
import {
  NotificationChannelEditorWizard,
  type NotificationChannelEditorFormState,
} from '@/views/notifications/NotificationChannelEditorWizard';
import {
  NotificationRecipientGroupEditorWizard,
  type NotificationRecipientGroupEditorFormState,
} from '@/views/notifications/NotificationRecipientGroupEditorWizard';
import {
  NotificationRouteEditorWizard,
  type NotificationRouteEditorFormState,
} from '@/views/notifications/NotificationRouteEditorWizard';

type NotificationSection = 'channels' | 'recipients' | 'routes' | 'activity';

const notificationEvents = [
  {
    event_type: 'commerce.order.confirmation.send_requested.v1',
    notification_type: 'order_confirmation',
    label: 'Order confirmation request',
  },
  {
    event_type: 'commerce.quote.created.v1',
    notification_type: 'quote_submitted',
    label: 'Quote customer acknowledgement',
  },
  {
    event_type: 'commerce.quote.created.v1',
    notification_type: 'quote_submitted_internal',
    label: 'Quote request submitted',
  },
  {
    event_type: 'commerce.quote.updated.v1',
    notification_type: 'quote_updated_internal',
    label: 'Quote updated',
  },
  {
    event_type: 'commerce.quote.approved.v1',
    notification_type: 'quote_approved_internal',
    label: 'Quote approved',
  },
  {
    event_type: 'commerce.quote.rejected.v1',
    notification_type: 'quote_rejected_internal',
    label: 'Quote rejected',
  },
  {
    event_type: 'commerce.quote.cancelled.v1',
    notification_type: 'quote_cancelled_internal',
    label: 'Quote cancelled',
  },
  {
    event_type: 'status.alert.group.firing.v1',
    notification_type: 'status_alert_firing',
    label: 'Status alert firing',
  },
  {
    event_type: 'status.alert.group.reminder.v1',
    notification_type: 'status_alert_reminder',
    label: 'Status alert reminder',
  },
  {
    event_type: 'status.alert.group.resolved.v1',
    notification_type: 'status_alert_resolved',
    label: 'Status alert resolved',
  },
] as const;

function normalizeNotificationEventType(eventType: string, notificationType?: string): string {
  if (
    (notificationType === 'quote_submitted' || notificationType === 'quote_submitted_internal') &&
    eventType === 'commerce.quote.submitted.v1'
  ) {
    return 'commerce.quote.created.v1';
  }
  return eventType;
}

const primaryButtonClass =
  'inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButtonClass =
  'inline-flex items-center gap-2 rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-bold text-foreground transition-all hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-50';

const emptyList = <T,>(): NotificationListResponse<T> => ({
  items: [],
  total: 0,
  limit: 10,
  offset: 0,
  sort: 'created_at',
  direction: 'desc',
});

function emptyChannelForm(): NotificationChannelEditorFormState {
  return {
    channel_key: '',
    display_name: '',
    enabled: true,
    default_from_email: '',
    host: '',
    port: '1025',
    secure: false,
    username: '',
    password: '',
  };
}

function emptyRouteForm(channelOptions: CommerceSelectOptionViewModel[]): NotificationRouteEditorFormState {
  const firstEvent = notificationEvents[0];
  return {
    event_type: firstEvent.event_type,
    notification_type: firstEvent.notification_type,
    channel_id: channelOptions[0]?.value ?? '',
    enabled: true,
    priority: '100',
    quote_kind: '',
    status: '',
    previous_status: '',
    workflow_stage: '',
	severity: '',
	rule_key: '',
	resource_kind: '',
	site_id: '',
	device_role: '',
    recipient_group_ids: [],
    include_customer_email: firstEvent.notification_type === 'order_confirmation' || firstEvent.notification_type === 'quote_submitted',
  };
}

function emptyRecipientGroupForm(): NotificationRecipientGroupEditorFormState {
  return {
    group_key: '',
    display_name: '',
    enabled: true,
    members: [{ email: '', display_name: '', enabled: true }],
  };
}

function formatDate(value?: string | null): string {
  if (!value) {
    return 'Not recorded';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function getEventLabel(eventType: string, notificationType?: string): string {
  const normalizedEventType = normalizeNotificationEventType(eventType, notificationType);
  return (
    notificationEvents.find(
      event => event.event_type === normalizedEventType && (!notificationType || event.notification_type === notificationType),
    )?.label ?? normalizedEventType
  );
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'sent':
    case 'enabled':
      return 'success';
    case 'pending':
    case 'degraded':
      return 'warning';
    case 'failed':
    case 'disabled':
    case 'credentials missing':
      return 'error';
    case 'skipped':
      return 'muted';
    default:
      return 'default';
  }
}

function getFilterValues(columnFilters: ColumnFiltersState, key: string): string[] {
  const filter = columnFilters.find(entry => entry.id === key);
  return Array.isArray(filter?.value)
    ? filter.value.filter((value): value is string => typeof value === 'string')
    : [];
}

function getSingleFilterValue(columnFilters: ColumnFiltersState, key: string): string | undefined {
  const values = getFilterValues(columnFilters, key);
  return values.length === 1 ? values[0] : undefined;
}

function getBooleanFilterValue(columnFilters: ColumnFiltersState, key: string): boolean | undefined {
  const value = getSingleFilterValue(columnFilters, key);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

function getSortField(
  sorting: SortingState,
  fallback: string,
  allowed: Record<string, string>,
): string {
  if (!sorting.length) {
    return fallback;
  }
  return allowed[sorting[0].id] ?? fallback;
}

function getSortDirection(
  sorting: SortingState,
  fallback: 'asc' | 'desc',
): 'asc' | 'desc' {
  if (!sorting.length) {
    return fallback;
  }
  return sorting[0].desc ? 'desc' : 'asc';
}

function channelRowToForm(channel: NotificationChannel): NotificationChannelEditorFormState {
  return {
    id: channel.id,
    channel_key: channel.channel_key,
    display_name: channel.display_name,
    enabled: channel.enabled,
    default_from_email: channel.default_from_email ?? '',
    host: channel.config.host ?? '',
    port: channel.config.port ? String(channel.config.port) : '1025',
    secure: Boolean(channel.config.secure),
    username: '',
    password: '',
  };
}

function routeRowToForm(route: NotificationRoute): NotificationRouteEditorFormState {
  const filter = route.filter_json ?? {};
  const targets = route.targets ?? [];
  const normalizedEventType = normalizeNotificationEventType(route.event_type, route.notification_type);
  return {
    id: route.id,
    event_type: normalizedEventType,
    notification_type: route.notification_type,
    channel_id: route.channel_id,
    enabled: route.enabled,
    priority: String(route.priority),
    quote_kind: typeof filter.quote_kind === 'object' && filter.quote_kind && 'value' in filter.quote_kind ? String((filter.quote_kind as { value?: string }).value ?? '') : '',
    status: typeof filter.status === 'object' && filter.status && 'value' in filter.status ? String((filter.status as { value?: string }).value ?? '') : '',
    previous_status: typeof filter.previous_status === 'object' && filter.previous_status && 'value' in filter.previous_status ? String((filter.previous_status as { value?: string }).value ?? '') : '',
    workflow_stage: typeof filter.workflow_stage === 'object' && filter.workflow_stage && 'value' in filter.workflow_stage ? String((filter.workflow_stage as { value?: string }).value ?? '') : '',
	severity: typeof filter.severity === 'object' && filter.severity && 'value' in filter.severity ? String((filter.severity as { value?: string }).value ?? '') : '',
	rule_key: typeof filter.rule_key === 'object' && filter.rule_key && 'value' in filter.rule_key ? String((filter.rule_key as { value?: string }).value ?? '') : '',
	resource_kind: typeof filter.resource_kind === 'object' && filter.resource_kind && 'value' in filter.resource_kind ? String((filter.resource_kind as { value?: string }).value ?? '') : '',
	site_id: typeof filter.site_id === 'object' && filter.site_id && 'value' in filter.site_id ? String((filter.site_id as { value?: string }).value ?? '') : '',
	device_role: typeof filter.device_role === 'object' && filter.device_role && 'value' in filter.device_role ? String((filter.device_role as { value?: string }).value ?? '') : '',
    recipient_group_ids: targets.filter(target => target.target_kind === 'recipient_group' && target.recipient_group_id).map(target => target.recipient_group_id as string),
    include_customer_email: targets.some(target => target.target_kind === 'customer_email'),
  };
}

function recipientGroupRowToForm(group: NotificationRecipientGroup): NotificationRecipientGroupEditorFormState {
  return {
    id: group.id,
    group_key: group.group_key,
    display_name: group.display_name,
    enabled: group.enabled,
    members: group.members.map(member => ({
      id: member.id,
      email: member.email,
      display_name: member.display_name ?? '',
      enabled: member.enabled,
    })),
  };
}

interface NotificationsPageProps {
  section: NotificationSection;
}

export default function NotificationsPage({ section }: NotificationsPageProps) {
  const { activeTenant } = useScope();
  const { session } = useSession();
  const notificationAccess = useNotificationAccess();

  const [channelsPage, setChannelsPage] = useState<NotificationListResponse<NotificationChannel>>(() => emptyList());
  const [recipientGroupsPage, setRecipientGroupsPage] = useState<NotificationListResponse<NotificationRecipientGroup>>(() => emptyList());
  const [routesPage, setRoutesPage] = useState<NotificationListResponse<NotificationRoute>>(() => emptyList());
  const [deliveriesPage, setDeliveriesPage] = useState<NotificationListResponse<NotificationDelivery>>(() => emptyList());
  const [channelOptions, setChannelOptions] = useState<CommerceSelectOptionViewModel[]>([]);
  const [recipientGroupOptions, setRecipientGroupOptions] = useState<CommerceSelectOptionViewModel[]>([]);

  const [channelLoading, setChannelLoading] = useState(false);
  const [recipientGroupLoading, setRecipientGroupLoading] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [testingChannelID, setTestingChannelID] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [channelPagination, setChannelPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [recipientGroupPagination, setRecipientGroupPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [routePagination, setRoutePagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [activityPagination, setActivityPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  const [channelSorting, setChannelSorting] = useState<SortingState>([{ id: 'display_name', desc: false }]);
  const [recipientGroupSorting, setRecipientGroupSorting] = useState<SortingState>([{ id: 'display_name', desc: false }]);
  const [routeSorting, setRouteSorting] = useState<SortingState>([{ id: 'priority', desc: false }]);
  const [activitySorting, setActivitySorting] = useState<SortingState>([{ id: 'updated_at', desc: true }]);

  const [channelFilters, setChannelFilters] = useState<ColumnFiltersState>([]);
  const [recipientGroupFilters, setRecipientGroupFilters] = useState<ColumnFiltersState>([]);
  const [routeFilters, setRouteFilters] = useState<ColumnFiltersState>([]);
  const [activityFilters, setActivityFilters] = useState<ColumnFiltersState>([]);

  const [channelSearch, setChannelSearch] = useState('');
  const [recipientGroupSearch, setRecipientGroupSearch] = useState('');
  const [routeSearch, setRouteSearch] = useState('');
  const [activitySearch, setActivitySearch] = useState('');

  const [channelEditorOpen, setChannelEditorOpen] = useState(false);
  const [recipientGroupEditorOpen, setRecipientGroupEditorOpen] = useState(false);
  const [routeEditorOpen, setRouteEditorOpen] = useState(false);
  const [savingChannel, setSavingChannel] = useState(false);
  const [savingRecipientGroup, setSavingRecipientGroup] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [channelForm, setChannelForm] = useState<NotificationChannelEditorFormState>(() => emptyChannelForm());
  const [recipientGroupForm, setRecipientGroupForm] = useState<NotificationRecipientGroupEditorFormState>(() => emptyRecipientGroupForm());
  const [routeForm, setRouteForm] = useState<NotificationRouteEditorFormState>(() => emptyRouteForm([]));

  const accessGranted = notificationAccess.notificationPermissionGranted;
  const canLoad = Boolean(activeTenant?.id && accessGranted);

  const sectionTitle = section === 'channels' ? 'Channels' : section === 'recipients' ? 'Recipients' : section === 'routes' ? 'Routes' : 'Activity';
  const sectionDescription =
    section === 'channels'
      ? 'Manage tenant SMTP delivery channels and test the active email path.'
      : section === 'recipients'
        ? 'Create reusable recipient groups for internal alert fan-out and operational distribution lists.'
      : section === 'routes'
        ? 'Map supported notification contracts to tenant delivery channels.'
        : 'Review recent delivery outcomes with the same operational tooling used across portal workspaces.';

  const accessMessage = useMemo(() => {
    if (!activeTenant?.id) {
      return 'Select a tenant to manage notification channels, routes, and recent delivery activity.';
    }
    if (!accessGranted) {
      return 'Your current session does not include notification administration permissions.';
    }
    return null;
  }, [accessGranted, activeTenant?.id]);

  const loadChannelOptions = useCallback(async () => {
    if (!canLoad) {
      setChannelOptions([]);
      return;
    }
    const response = await listNotificationChannels({
      limit: 200,
      offset: 0,
      sort: 'display_name',
      direction: 'asc',
    });
    setChannelOptions(
      response.items.map(channel => ({
        value: channel.id,
        label: channel.display_name,
        description: `${channel.channel_key} · ${channel.default_from_email || channel.config.host || 'No sender configured'}`,
      })),
    );
  }, [canLoad]);

  const loadRecipientGroupOptions = useCallback(async () => {
    if (!canLoad) {
      setRecipientGroupOptions([]);
      return;
    }
    const response = await listNotificationRecipientGroups({
      limit: 200,
      offset: 0,
      sort: 'display_name',
      direction: 'asc',
    });
    setRecipientGroupOptions(
      response.items.map(group => ({
        value: group.id,
        label: group.display_name,
        description: `${group.group_key} · ${group.member_count} ${group.member_count === 1 ? 'member' : 'members'}`,
      })),
    );
  }, [canLoad]);

  const loadChannels = useCallback(async () => {
    if (!canLoad) {
      setChannelsPage(emptyList());
      return;
    }
    setChannelLoading(true);
    setError(null);
    try {
      const params: NotificationChannelListParams = {
        ...toLimitOffset(channelPagination),
        enabled: getBooleanFilterValue(channelFilters, 'enabled'),
        channelType: getSingleFilterValue(channelFilters, 'channel_type'),
        q: channelSearch.trim() || undefined,
        sort: getSortField(channelSorting, 'display_name', {
          display_name: 'display_name',
          channel_key: 'channel_key',
          updated_at: 'updated_at',
          created_at: 'created_at',
        }) as NotificationChannelListParams['sort'],
        direction: getSortDirection(channelSorting, 'asc'),
      };
      const response = await listNotificationChannels(params);
      const nextPageIndex = lastPageIndexForTotal(response.total, channelPagination.pageSize);
      if (channelPagination.pageIndex > nextPageIndex) {
        setChannelPagination(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setChannelsPage(response);
    } catch (err) {
      setChannelsPage(emptyList());
      setError(getNotificationAdminErrorMessage(err, 'Failed to load notification channels.'));
    } finally {
      setChannelLoading(false);
    }
  }, [canLoad, channelFilters, channelPagination, channelSearch, channelSorting]);

  const loadRoutes = useCallback(async () => {
    if (!canLoad) {
      setRoutesPage(emptyList());
      return;
    }
    setRouteLoading(true);
    setError(null);
    try {
      const params: NotificationRouteListParams = {
        ...toLimitOffset(routePagination),
        enabled: getBooleanFilterValue(routeFilters, 'enabled'),
        eventType: getSingleFilterValue(routeFilters, 'event_type'),
        q: routeSearch.trim() || undefined,
        sort: getSortField(routeSorting, 'priority', {
          priority: 'priority',
          event_type: 'event_type',
          updated_at: 'updated_at',
          created_at: 'created_at',
        }) as NotificationRouteListParams['sort'],
        direction: getSortDirection(routeSorting, 'asc'),
      };
      const response = await listNotificationRoutes(params);
      const nextPageIndex = lastPageIndexForTotal(response.total, routePagination.pageSize);
      if (routePagination.pageIndex > nextPageIndex) {
        setRoutePagination(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setRoutesPage(response);
    } catch (err) {
      setRoutesPage(emptyList());
      setError(getNotificationAdminErrorMessage(err, 'Failed to load notification routes.'));
    } finally {
      setRouteLoading(false);
    }
  }, [canLoad, routeFilters, routePagination, routeSearch, routeSorting]);

  const loadRecipientGroups = useCallback(async () => {
    if (!canLoad) {
      setRecipientGroupsPage(emptyList());
      return;
    }
    setRecipientGroupLoading(true);
    setError(null);
    try {
      const params: NotificationRecipientGroupListParams = {
        ...toLimitOffset(recipientGroupPagination),
        enabled: getBooleanFilterValue(recipientGroupFilters, 'enabled'),
        q: recipientGroupSearch.trim() || undefined,
        sort: getSortField(recipientGroupSorting, 'display_name', {
          display_name: 'display_name',
          group_key: 'group_key',
          updated_at: 'updated_at',
          created_at: 'created_at',
        }) as NotificationRecipientGroupListParams['sort'],
        direction: getSortDirection(recipientGroupSorting, 'asc'),
      };
      const response = await listNotificationRecipientGroups(params);
      const nextPageIndex = lastPageIndexForTotal(response.total, recipientGroupPagination.pageSize);
      if (recipientGroupPagination.pageIndex > nextPageIndex) {
        setRecipientGroupPagination(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setRecipientGroupsPage(response);
    } catch (err) {
      setRecipientGroupsPage(emptyList());
      setError(getNotificationAdminErrorMessage(err, 'Failed to load notification recipient groups.'));
    } finally {
      setRecipientGroupLoading(false);
    }
  }, [canLoad, recipientGroupFilters, recipientGroupPagination, recipientGroupSearch, recipientGroupSorting]);

  const loadDeliveries = useCallback(async () => {
    if (!canLoad) {
      setDeliveriesPage(emptyList());
      return;
    }
    setActivityLoading(true);
    setError(null);
    try {
      const params: NotificationDeliveryListParams = {
        ...toLimitOffset(activityPagination),
        status: getSingleFilterValue(activityFilters, 'status'),
        eventType: getSingleFilterValue(activityFilters, 'event_type'),
        recipient: activitySearch.trim() || undefined,
        sort: getSortField(activitySorting, 'updated_at', {
          updated_at: 'updated_at',
          created_at: 'created_at',
          last_attempt_at: 'last_attempt_at',
          status: 'status',
        }) as NotificationDeliveryListParams['sort'],
        direction: getSortDirection(activitySorting, 'desc'),
      };
      const response = await listNotificationDeliveries(params);
      const nextPageIndex = lastPageIndexForTotal(response.total, activityPagination.pageSize);
      if (activityPagination.pageIndex > nextPageIndex) {
        setActivityPagination(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setDeliveriesPage(response);
    } catch (err) {
      setDeliveriesPage(emptyList());
      setError(getNotificationAdminErrorMessage(err, 'Failed to load notification activity.'));
    } finally {
      setActivityLoading(false);
    }
  }, [activityFilters, activityPagination, activitySearch, activitySorting, canLoad]);

  useEffect(() => {
    if (!canLoad) {
      setChannelsPage(emptyList());
      setRecipientGroupsPage(emptyList());
      setRoutesPage(emptyList());
      setDeliveriesPage(emptyList());
      setChannelOptions([]);
      setRecipientGroupOptions([]);
      return;
    }
    void loadChannelOptions();
    void loadRecipientGroupOptions();
  }, [canLoad, loadChannelOptions, loadRecipientGroupOptions]);

  useEffect(() => {
    if (section === 'channels') {
      void loadChannels();
    }
  }, [loadChannels, section]);

  useEffect(() => {
    if (section === 'recipients') {
      void loadRecipientGroups();
    }
  }, [loadRecipientGroups, section]);

  useEffect(() => {
    if (section === 'routes') {
      void loadRoutes();
    }
  }, [loadRoutes, section]);

  useEffect(() => {
    if (section === 'activity') {
      void loadDeliveries();
    }
  }, [loadDeliveries, section]);

  useEffect(() => {
    setChannelPagination(current => ({ ...current, pageIndex: 0 }));
  }, [channelFilters, channelSearch, channelSorting]);

  useEffect(() => {
    setRecipientGroupPagination(current => ({ ...current, pageIndex: 0 }));
  }, [recipientGroupFilters, recipientGroupSearch, recipientGroupSorting]);

  useEffect(() => {
    setRoutePagination(current => ({ ...current, pageIndex: 0 }));
  }, [routeFilters, routeSearch, routeSorting]);

  useEffect(() => {
    setActivityPagination(current => ({ ...current, pageIndex: 0 }));
  }, [activityFilters, activitySearch, activitySorting]);

  const openCreateChannel = useCallback(() => {
    setChannelForm(emptyChannelForm());
    setChannelEditorOpen(true);
  }, []);

  const openCreateRecipientGroup = useCallback(() => {
    setRecipientGroupForm(emptyRecipientGroupForm());
    setRecipientGroupEditorOpen(true);
  }, []);

  const openEditChannel = useCallback((channel: NotificationChannel) => {
    setChannelForm(channelRowToForm(channel));
    setChannelEditorOpen(true);
  }, []);

  const openEditRecipientGroup = useCallback((group: NotificationRecipientGroup) => {
    setRecipientGroupForm(recipientGroupRowToForm(group));
    setRecipientGroupEditorOpen(true);
  }, []);

  const openCreateRoute = useCallback(() => {
    setRouteForm(emptyRouteForm(channelOptions));
    setRouteEditorOpen(true);
  }, [channelOptions]);

  const openEditRoute = useCallback((route: NotificationRoute) => {
    setRouteForm(routeRowToForm(route));
    setRouteEditorOpen(true);
  }, []);

  const handleSaveChannel = useCallback(async () => {
    const payload: NotificationChannelPayload = {
      channel_key: channelForm.channel_key.trim(),
      channel_type: 'email_smtp',
      display_name: channelForm.display_name.trim(),
      enabled: channelForm.enabled,
      default_from_email: channelForm.default_from_email.trim(),
      config: {
        host: channelForm.host.trim(),
        port: Number(channelForm.port || '0') || 1025,
        secure: channelForm.secure,
        from_email: channelForm.default_from_email.trim(),
      },
    };
    if (channelForm.username.trim() || channelForm.password.trim()) {
      payload.secret_payload = {
        username: channelForm.username.trim(),
        password: channelForm.password,
      };
    }

    setSavingChannel(true);
    try {
      if (channelForm.id) {
        await updateNotificationChannel(channelForm.id, payload);
        toast.success('Notification channel updated.');
      } else {
        await createNotificationChannel(payload);
        toast.success('Notification channel created.');
      }
      setChannelEditorOpen(false);
      setChannelForm(emptyChannelForm());
      await Promise.all([loadChannels(), loadChannelOptions()]);
    } catch (err) {
      toast.error(getNotificationAdminErrorMessage(err, 'Failed to save notification channel.'));
    } finally {
      setSavingChannel(false);
    }
  }, [channelForm, loadChannelOptions, loadChannels]);

  const handleSaveRoute = useCallback(async () => {
    const filter_json: Record<string, { op: 'equals'; value: string }> = {};
	const isStatusEvent = routeForm.event_type.startsWith('status.alert.');
	if (!isStatusEvent && routeForm.quote_kind.trim()) {
      filter_json.quote_kind = { op: 'equals', value: routeForm.quote_kind.trim() };
    }
    if (routeForm.status.trim()) {
      filter_json.status = { op: 'equals', value: routeForm.status.trim() };
    }
	if (!isStatusEvent && routeForm.previous_status.trim()) {
      filter_json.previous_status = { op: 'equals', value: routeForm.previous_status.trim() };
    }
	if (!isStatusEvent && routeForm.workflow_stage.trim()) {
      filter_json.workflow_stage = { op: 'equals', value: routeForm.workflow_stage.trim() };
    }
	if (isStatusEvent && routeForm.severity.trim()) filter_json.severity = { op: 'equals', value: routeForm.severity.trim() };
	if (isStatusEvent && routeForm.rule_key.trim()) filter_json.rule_key = { op: 'equals', value: routeForm.rule_key.trim() };
	if (isStatusEvent && routeForm.resource_kind.trim()) filter_json.resource_kind = { op: 'equals', value: routeForm.resource_kind.trim() };
	if (isStatusEvent && routeForm.site_id.trim()) filter_json.site_id = { op: 'equals', value: routeForm.site_id.trim() };
	if (isStatusEvent && routeForm.device_role.trim()) filter_json.device_role = { op: 'equals', value: routeForm.device_role.trim() };
    const payload: NotificationRoutePayload = {
      event_type: routeForm.event_type,
      notification_type: routeForm.notification_type,
      channel_id: routeForm.channel_id,
      enabled: routeForm.enabled,
      priority: Number(routeForm.priority || '0') || 100,
      filter_json,
      targets: [
        ...(routeForm.include_customer_email ? [{ target_kind: 'customer_email' as const, target_ref: 'customer_email', enabled: true }] : []),
        ...routeForm.recipient_group_ids.map(groupId => ({ target_kind: 'recipient_group' as const, recipient_group_id: groupId, enabled: true })),
      ],
    };
    setSavingRoute(true);
    try {
      if (routeForm.id) {
        await updateNotificationRoute(routeForm.id, payload);
        toast.success('Notification route updated.');
      } else {
        await createNotificationRoute(payload);
        toast.success('Notification route created.');
      }
      setRouteEditorOpen(false);
      setRouteForm(emptyRouteForm(channelOptions));
      await loadRoutes();
    } catch (err) {
      toast.error(getNotificationAdminErrorMessage(err, 'Failed to save notification route.'));
    } finally {
      setSavingRoute(false);
    }
  }, [channelOptions, loadRoutes, routeForm]);

  const handleSaveRecipientGroup = useCallback(async () => {
    const payload: NotificationRecipientGroupPayload = {
      group_key: recipientGroupForm.group_key.trim(),
      display_name: recipientGroupForm.display_name.trim(),
      enabled: recipientGroupForm.enabled,
      members: recipientGroupForm.members
        .filter(member => member.email.trim())
        .map(member => ({
          member_kind: 'email',
          email: member.email.trim(),
          display_name: member.display_name.trim() || undefined,
          enabled: member.enabled,
        })),
    };
    setSavingRecipientGroup(true);
    try {
      if (recipientGroupForm.id) {
        await updateNotificationRecipientGroup(recipientGroupForm.id, payload);
        toast.success('Recipient group updated.');
      } else {
        await createNotificationRecipientGroup(payload);
        toast.success('Recipient group created.');
      }
      setRecipientGroupEditorOpen(false);
      setRecipientGroupForm(emptyRecipientGroupForm());
      await Promise.all([loadRecipientGroups(), loadRecipientGroupOptions()]);
    } catch (err) {
      toast.error(getNotificationAdminErrorMessage(err, 'Failed to save recipient group.'));
    } finally {
      setSavingRecipientGroup(false);
    }
  }, [loadRecipientGroupOptions, loadRecipientGroups, recipientGroupForm]);

  const handleDeleteRoute = useCallback(async (routeId: string) => {
    try {
      await deleteNotificationRoute(routeId);
      toast.success('Notification route deleted.');
      await loadRoutes();
    } catch (err) {
      toast.error(getNotificationAdminErrorMessage(err, 'Failed to delete notification route.'));
    }
  }, [loadRoutes]);

  const handleDeleteRecipientGroup = useCallback(async (groupId: string) => {
    try {
      await deleteNotificationRecipientGroup(groupId);
      toast.success('Recipient group deleted.');
      await Promise.all([loadRecipientGroups(), loadRecipientGroupOptions()]);
    } catch (err) {
      toast.error(getNotificationAdminErrorMessage(err, 'Failed to delete recipient group.'));
    }
  }, [loadRecipientGroupOptions, loadRecipientGroups]);

  const handleTestChannel = useCallback(async (channelId: string) => {
    const recipientEmail = session?.email?.trim();
    if (!recipientEmail) {
      toast.error('Current user email is unavailable for test sends.');
      return;
    }
    setTestingChannelID(channelId);
    try {
      await testNotificationChannel(channelId, recipientEmail, session?.username);
      toast.success('Test email queued.');
      await loadDeliveries();
    } catch (err) {
      toast.error(getNotificationAdminErrorMessage(err, 'Failed to send test email.'));
    } finally {
      setTestingChannelID(null);
    }
  }, [loadDeliveries, session?.email, session?.username]);

  const channelColumns = useMemo<ColumnDef<NotificationChannel>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'Channel',
        cell: ({ row }) => (
          <button type="button" onClick={() => openEditChannel(row.original)} className="text-left">
            <div className="font-bold text-foreground">{row.original.display_name}</div>
            <div className="mt-1 text-xs text-muted-foreground">{row.original.channel_key}</div>
          </button>
        ),
      },
      {
        accessorKey: 'default_from_email',
        header: 'Sender',
        cell: ({ row }) => row.original.default_from_email || 'Not set',
      },
      {
        accessorKey: 'channel_type',
        header: 'Transport',
        cell: ({ row }) => (
          <VerticalStack gap={0.5}>
            <span className="font-medium text-foreground">{row.original.channel_type}</span>
            <span className="text-xs text-muted-foreground">{row.original.config.host}:{row.original.config.port ?? 1025}</span>
          </VerticalStack>
        ),
      },
      {
        accessorKey: 'enabled',
        header: 'Status',
        cell: ({ row }) => (
          <VerticalStack gap={1}>
            <RegistryStatus label={row.original.enabled ? 'Enabled' : 'Disabled'} variant={statusVariant(row.original.enabled ? 'enabled' : 'disabled')} />
            <RegistryStatus
              label={row.original.secret_configured ? 'Ready' : 'Credentials missing'}
              variant={statusVariant(row.original.secret_configured ? 'sent' : 'credentials missing')}
            />
          </VerticalStack>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => formatDate(row.original.updated_at),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <HorizontalStack gap={2}>
            <button type="button" className={secondaryButtonClass} onClick={() => openEditChannel(row.original)}>
              Edit
            </button>
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => {
                void handleTestChannel(row.original.id);
              }}
            >
              {testingChannelID === row.original.id ? 'Sending…' : 'Test'}
            </button>
          </HorizontalStack>
        ),
      },
    ],
    [handleTestChannel, openEditChannel, testingChannelID],
  );

  const routeColumns = useMemo<ColumnDef<NotificationRoute>[]>(
    () => [
      {
        accessorKey: 'event_type',
        header: 'Event',
        cell: ({ row }) => (
          <button type="button" onClick={() => openEditRoute(row.original)} className="text-left">
            <div className="font-bold text-foreground">{getEventLabel(row.original.event_type, row.original.notification_type)}</div>
            <div className="mt-1 text-xs text-muted-foreground">{row.original.event_type}</div>
          </button>
        ),
      },
      {
        accessorKey: 'channel_display_name',
        header: 'Channel',
        cell: ({ row }) => (
          <VerticalStack gap={0.5}>
            <span className="font-medium text-foreground">{row.original.channel_display_name}</span>
            <span className="text-xs text-muted-foreground">{row.original.channel_key}</span>
          </VerticalStack>
        ),
      },
      {
        id: 'targets',
        header: 'Targets',
        cell: ({ row }) => {
          const targets = row.original.targets ?? [];
          const targetCount = row.original.target_count ?? targets.length;
          return (
            <VerticalStack gap={0.5}>
              <span className="font-medium text-foreground">
                {targets.map(target => target.target_kind === 'customer_email' ? 'Customer email' : target.recipient_group_name || target.recipient_group_key).join(', ') || 'No targets'}
              </span>
              <span className="text-xs text-muted-foreground">{targetCount} target{targetCount === 1 ? '' : 's'}</span>
            </VerticalStack>
          );
        },
      },
      {
        accessorKey: 'priority',
        header: 'Priority',
      },
      {
        accessorKey: 'enabled',
        header: 'Status',
        cell: ({ row }) => (
          <RegistryStatus label={row.original.enabled ? 'Enabled' : 'Disabled'} variant={statusVariant(row.original.enabled ? 'enabled' : 'disabled')} />
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => formatDate(row.original.updated_at),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <HorizontalStack gap={2}>
            <button type="button" className={secondaryButtonClass} onClick={() => openEditRoute(row.original)}>
              Edit
            </button>
            <button type="button" className={secondaryButtonClass} onClick={() => void handleDeleteRoute(row.original.id)}>
              Delete
            </button>
          </HorizontalStack>
        ),
      },
    ],
    [handleDeleteRoute, openEditRoute],
  );

  const recipientGroupColumns = useMemo<ColumnDef<NotificationRecipientGroup>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'Recipient Group',
        cell: ({ row }) => (
          <button type="button" onClick={() => openEditRecipientGroup(row.original)} className="text-left">
            <div className="font-bold text-foreground">{row.original.display_name}</div>
            <div className="mt-1 text-xs text-muted-foreground">{row.original.group_key}</div>
          </button>
        ),
      },
      {
        accessorKey: 'member_count',
        header: 'Members',
      },
      {
        accessorKey: 'enabled',
        header: 'Status',
        cell: ({ row }) => (
          <RegistryStatus label={row.original.enabled ? 'Enabled' : 'Disabled'} variant={statusVariant(row.original.enabled ? 'enabled' : 'disabled')} />
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => formatDate(row.original.updated_at),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <HorizontalStack gap={2}>
            <button type="button" className={secondaryButtonClass} onClick={() => openEditRecipientGroup(row.original)}>
              Edit
            </button>
            <button type="button" className={secondaryButtonClass} onClick={() => void handleDeleteRecipientGroup(row.original.id)}>
              Delete
            </button>
          </HorizontalStack>
        ),
      },
    ],
    [handleDeleteRecipientGroup, openEditRecipientGroup],
  );

  const activityColumns = useMemo<ColumnDef<NotificationDelivery>[]>(
    () => [
      {
        accessorKey: 'updated_at',
        header: 'Time',
        cell: ({ row }) => formatDate(row.original.updated_at ?? row.original.created_at),
      },
      {
        accessorKey: 'event_type',
        header: 'Event',
        cell: ({ row }) => (
          <VerticalStack gap={0.5}>
            <span className="font-bold text-foreground">{getEventLabel(row.original.event_type, row.original.notification_type)}</span>
            <span className="text-xs text-muted-foreground">{row.original.event_type}</span>
          </VerticalStack>
        ),
      },
      {
        accessorKey: 'recipient_email',
        header: 'Recipient',
        cell: ({ row }) => (
          <VerticalStack gap={0.5}>
            <span className="font-medium text-foreground">{row.original.recipient_email}</span>
            {row.original.recipient_name ? <span className="text-xs text-muted-foreground">{row.original.recipient_name}</span> : null}
          </VerticalStack>
        ),
      },
      {
        accessorKey: 'channel_key',
        header: 'Channel',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <VerticalStack gap={1}>
            <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
            {row.original.error_message ? (
              <span className="text-xs text-destructive">{row.original.error_message}</span>
            ) : null}
          </VerticalStack>
        ),
      },
      {
        accessorKey: 'attempt_count',
        header: 'Attempts',
      },
    ],
    [],
  );

  const currentLoading = section === 'channels' ? channelLoading : section === 'recipients' ? recipientGroupLoading : section === 'routes' ? routeLoading : activityLoading;
  const sectionCount = section === 'channels' ? channelsPage.total : section === 'recipients' ? recipientGroupsPage.total : section === 'routes' ? routesPage.total : deliveriesPage.total;
  const takeoverContent = channelEditorOpen ? (
    <NotificationChannelEditorWizard
      isOpen={channelEditorOpen}
      form={channelForm}
      setForm={setChannelForm}
      saving={savingChannel}
      onSave={handleSaveChannel}
      onClose={() => {
        setChannelEditorOpen(false);
        setChannelForm(emptyChannelForm());
      }}
    />
  ) : recipientGroupEditorOpen ? (
    <NotificationRecipientGroupEditorWizard
      isOpen={recipientGroupEditorOpen}
      form={recipientGroupForm}
      setForm={setRecipientGroupForm}
      saving={savingRecipientGroup}
      onSave={handleSaveRecipientGroup}
      onClose={() => {
        setRecipientGroupEditorOpen(false);
        setRecipientGroupForm(emptyRecipientGroupForm());
      }}
    />
  ) : routeEditorOpen ? (
    <NotificationRouteEditorWizard
      isOpen={routeEditorOpen}
      form={routeForm}
      setForm={setRouteForm}
      channelOptions={channelOptions}
      recipientGroupOptions={recipientGroupOptions}
      eventOptions={[...notificationEvents]}
      saving={savingRoute}
      onSave={handleSaveRoute}
      onClose={() => {
        setRouteEditorOpen(false);
        setRouteForm(emptyRouteForm(channelOptions));
      }}
    />
  ) : null;
  const takeoverActive = Boolean(takeoverContent);

  const sectionSurface = !accessMessage ? (
    <Surface className="flex min-h-0 flex-1 flex-col overflow-hidden border-border/60 bg-background shadow-none">
      <div className="border-b border-border/40 px-4 py-3">
        <HorizontalStack className="items-start justify-between gap-4">
          <VerticalStack gap={1}>
            <h2 className="text-sm font-black uppercase tracking-widest text-foreground">{sectionTitle}</h2>
            <DescriptionText className="max-w-3xl text-xs">{sectionDescription}</DescriptionText>
          </VerticalStack>
          <RegistryStatus label={`${sectionCount} ${sectionCount === 1 ? 'record' : 'records'}`} variant="muted" />
        </HorizontalStack>
      </div>

      {section === 'channels' ? (
        <DataTable
          mode="manual"
          columns={channelColumns}
          data={channelsPage.items}
          sorting={channelSorting}
          onSortingChange={setChannelSorting}
          columnFilters={channelFilters}
          onColumnFiltersChange={setChannelFilters}
          globalFilter={channelSearch}
          onGlobalFilterChange={(value) => setChannelSearch(String(value ?? ''))}
          pagination={channelPagination}
          onPaginationChange={setChannelPagination}
          totalRowCount={channelsPage.total}
          loading={channelLoading}
          className="border-none rounded-none"
          paginationClassName="px-0 py-0"
          showPagination={true}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              className="border-b border-border/40 bg-transparent px-0 py-0"
              searchPlaceholder="Search channels..."
              searchDebounceMs={250}
              filters={(
                <>
                  {table.getColumn('enabled') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(table.getColumn('enabled')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('enabled')?.setFilterValue(Array.from(values))}
                      options={[
                        { label: 'Enabled', value: 'true' },
                        { label: 'Disabled', value: 'false' },
                      ]}
                    />
                  )}
                  {table.getColumn('channel_type') && (
                    <DataTableFacetedFilter
                      title="Transport"
                      selectedValues={new Set(table.getColumn('channel_type')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('channel_type')?.setFilterValue(Array.from(values))}
                      options={[{ label: 'SMTP', value: 'email_smtp' }]}
                    />
                  )}
                </>
              )}
            />
          )}
          emptyState={(
            <div className="px-0 py-12 text-center">
              <p className="text-sm font-black text-foreground">No channels yet</p>
              <DescriptionText className="mt-1 text-xs">
                No notification channels are configured for this tenant yet.
              </DescriptionText>
            </div>
          )}
          noResultsState="No channels matched the current filters."
        />
      ) : null}

      {section === 'recipients' ? (
        <DataTable
          mode="manual"
          columns={recipientGroupColumns}
          data={recipientGroupsPage.items}
          sorting={recipientGroupSorting}
          onSortingChange={setRecipientGroupSorting}
          columnFilters={recipientGroupFilters}
          onColumnFiltersChange={setRecipientGroupFilters}
          globalFilter={recipientGroupSearch}
          onGlobalFilterChange={(value) => setRecipientGroupSearch(String(value ?? ''))}
          pagination={recipientGroupPagination}
          onPaginationChange={setRecipientGroupPagination}
          totalRowCount={recipientGroupsPage.total}
          loading={recipientGroupLoading}
          className="border-none rounded-none"
          paginationClassName="px-0 py-0"
          showPagination={true}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              className="border-b border-border/40 bg-transparent px-0 py-0"
              searchPlaceholder="Search recipient groups..."
              searchDebounceMs={250}
              filters={(
                <>
                  {table.getColumn('enabled') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(table.getColumn('enabled')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('enabled')?.setFilterValue(Array.from(values))}
                      options={[
                        { label: 'Enabled', value: 'true' },
                        { label: 'Disabled', value: 'false' },
                      ]}
                    />
                  )}
                </>
              )}
            />
          )}
          emptyState={(
            <div className="px-0 py-12 text-center">
              <p className="text-sm font-black text-foreground">No recipient groups yet</p>
              <DescriptionText className="mt-1 text-xs">
                Create reusable distribution lists for quote alerts and customer notifications.
              </DescriptionText>
            </div>
          )}
          noResultsState="No recipient groups matched the current filters."
        />
      ) : null}

      {section === 'routes' ? (
        <DataTable
          mode="manual"
          columns={routeColumns}
          data={routesPage.items}
          sorting={routeSorting}
          onSortingChange={setRouteSorting}
          columnFilters={routeFilters}
          onColumnFiltersChange={setRouteFilters}
          globalFilter={routeSearch}
          onGlobalFilterChange={(value) => setRouteSearch(String(value ?? ''))}
          pagination={routePagination}
          onPaginationChange={setRoutePagination}
          totalRowCount={routesPage.total}
          loading={routeLoading}
          className="border-none rounded-none"
          paginationClassName="px-0 py-0"
          showPagination={true}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              className="border-b border-border/40 bg-transparent px-0 py-0"
              searchPlaceholder="Search routes..."
              searchDebounceMs={250}
              filters={(
                <>
                  {table.getColumn('enabled') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(table.getColumn('enabled')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('enabled')?.setFilterValue(Array.from(values))}
                      options={[
                        { label: 'Enabled', value: 'true' },
                        { label: 'Disabled', value: 'false' },
                      ]}
                    />
                  )}
                  {table.getColumn('event_type') && (
                    <DataTableFacetedFilter
                      title="Event"
                      selectedValues={new Set(table.getColumn('event_type')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('event_type')?.setFilterValue(Array.from(values))}
                      options={notificationEvents.map(event => ({ label: event.label, value: event.event_type }))}
                    />
                  )}
                </>
              )}
            />
          )}
          emptyState={(
            <div className="px-0 py-12 text-center">
              <p className="text-sm font-black text-foreground">No routes yet</p>
              <DescriptionText className="mt-1 text-xs">
                No tenant notification routes are configured yet.
              </DescriptionText>
            </div>
          )}
          noResultsState="No routes matched the current filters."
        />
      ) : null}

      {section === 'activity' ? (
        <DataTable
          mode="manual"
          columns={activityColumns}
          data={deliveriesPage.items}
          sorting={activitySorting}
          onSortingChange={setActivitySorting}
          columnFilters={activityFilters}
          onColumnFiltersChange={setActivityFilters}
          globalFilter={activitySearch}
          onGlobalFilterChange={(value) => setActivitySearch(String(value ?? ''))}
          pagination={activityPagination}
          onPaginationChange={setActivityPagination}
          totalRowCount={deliveriesPage.total}
          loading={activityLoading}
          className="border-none rounded-none"
          paginationClassName="px-0 py-0"
          showPagination={true}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              className="border-b border-border/40 bg-transparent px-0 py-0"
              searchPlaceholder="Search recipients..."
              searchDebounceMs={250}
              filters={(
                <>
                  {table.getColumn('status') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(table.getColumn('status')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('status')?.setFilterValue(Array.from(values))}
                      options={[
                        { label: 'Sent', value: 'sent' },
                        { label: 'Pending', value: 'pending' },
                        { label: 'Failed', value: 'failed' },
                        { label: 'Skipped', value: 'skipped' },
                      ]}
                    />
                  )}
                  {table.getColumn('event_type') && (
                    <DataTableFacetedFilter
                      title="Event"
                      selectedValues={new Set(table.getColumn('event_type')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('event_type')?.setFilterValue(Array.from(values))}
                      options={notificationEvents.map(event => ({ label: event.label, value: event.event_type }))}
                    />
                  )}
                </>
              )}
            />
          )}
          emptyState={(
            <div className="px-0 py-12 text-center">
              <p className="text-sm font-black text-foreground">No activity yet</p>
              <DescriptionText className="mt-1 text-xs">
                No recent notification deliveries have been recorded for this tenant.
              </DescriptionText>
            </div>
          )}
          noResultsState="No activity matched the current filters."
        />
      ) : null}
    </Surface>
  ) : (
    <Surface className="border-border/60 bg-muted/10 p-6">
      <VerticalStack gap={3}>
        <HorizontalStack gap={2} className="items-center text-muted-foreground">
          <ShieldAlert className="h-4 w-4" />
          <h2 className="text-sm font-black uppercase tracking-widest">Access required</h2>
        </HorizontalStack>
        <DescriptionText>{accessMessage}</DescriptionText>
      </VerticalStack>
    </Surface>
  );

  return (
    <>
      <AdminShell
        title={(
          <WorksurfaceHeader
            parentLabel="Notifications"
            activeLabel={sectionTitle}
            description={sectionDescription}
            icon={section === 'channels' ? <Mail className="h-4 w-4 text-primary" /> : <Route className="h-4 w-4 text-primary" />}
          />
        )}
        actions={takeoverActive ? null : (
          <HorizontalStack gap={2} className="items-center">
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => {
                if (section === 'channels') {
                  void loadChannels();
                  return;
                }
                if (section === 'recipients') {
                  void Promise.all([loadRecipientGroups(), loadRecipientGroupOptions()]);
                  return;
                }
                if (section === 'routes') {
                  void Promise.all([loadRoutes(), loadChannelOptions(), loadRecipientGroupOptions()]);
                  return;
                }
                void loadDeliveries();
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            {section === 'channels' ? (
              <button type="button" className={primaryButtonClass} onClick={openCreateChannel}>
                New Channel
              </button>
            ) : null}
            {section === 'recipients' ? (
              <button type="button" className={primaryButtonClass} onClick={openCreateRecipientGroup}>
                New Recipient Group
              </button>
            ) : null}
            {section === 'routes' ? (
              <button type="button" className={primaryButtonClass} onClick={openCreateRoute} disabled={channelOptions.length === 0}>
                New Route
              </button>
            ) : null}
          </HorizontalStack>
        )}
        className="bg-background"
        bodyClassName="flex min-h-0 flex-col p-0"
        headerClassName={takeoverActive ? 'hidden' : undefined}
        flat
      >
        {!takeoverActive && error ? (
          <Surface className="mb-0 border-x-0 border-t-0 border-b border-destructive/40 bg-destructive/5 p-4 shadow-none">
            <DescriptionText className="text-destructive">{error}</DescriptionText>
          </Surface>
        ) : null}
        <div className="relative flex min-h-0 flex-1 flex-col">
          {takeoverActive ? takeoverContent : sectionSurface}
        </div>
      </AdminShell>
    </>
  );
}

export function getNotificationAdminErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof NotificationAdminError) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}
